import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { db } from "services/firebase";
import { format } from "date-fns";
import { captureHandledError } from "config/sentry";

const PAGE_SIZE = 50;

export async function listPhiAccessLog({
  institutionId,
  startAfterDoc = null,
  limit = PAGE_SIZE,
}) {
  if (!institutionId) return { rows: [], lastDoc: null };

  let query = db
    .collection("phiAccessLog")
    .where("institutionId", "==", institutionId)
    .orderBy("timestamp", "desc")
    .limit(limit);

  if (startAfterDoc) {
    query = query.startAfter(startAfterDoc);
  }

  const snap = await query.get();
  const rows = snap.docs.map((doc) => {
    const data = doc.data();
    const ts = data.timestamp?.toDate?.() || null;
    return {
      id: doc.id,
      ...data,
      timestampLabel: ts ? format(ts, "MMM d, yyyy h:mm a") : "—",
      _doc: doc,
    };
  });

  return {
    rows,
    lastDoc: snap.docs.length ? snap.docs[snap.docs.length - 1] : null,
  };
}

export async function listPhiAccessLogForAdmin({ limit = 100 } = {}) {
  const snap = await db
    .collection("phiAccessLog")
    .orderBy("timestamp", "desc")
    .limit(limit)
    .get();

  return snap.docs.map((doc) => {
    const data = doc.data();
    const ts = data.timestamp?.toDate?.() || null;
    return {
      id: doc.id,
      ...data,
      timestampLabel: ts ? format(ts, "MMM d, yyyy h:mm a") : "—",
    };
  });
}

export async function recordSessionReplayAccess(sessionId, purpose = "session-replay") {
  const callable = firebase.app().functions("us-central1").httpsCallable("recordSessionReplayAccess");
  const result = await callable({ sessionId, purpose });
  return result.data;
}

/**
 * Fire-and-forget wrapper for logging PHI session-replay access.
 *
 * A failure to write the audit trail must NEVER block a clinician from doing
 * their job (visibility, not gating), but it must also never be silently
 * swallowed. On failure we surface a durable monitoring signal via Sentry
 * (`captureHandledError`) instead of dropping the error, and resolve to a status
 * object so callers can react if they want without needing a try/catch.
 *
 * @returns {Promise<{ logged: boolean, error?: unknown }>}
 */
export async function logSessionReplayAccessSafely(sessionId, purpose = "session-replay") {
  try {
    await recordSessionReplayAccess(sessionId, purpose);
    return { logged: true };
  } catch (error) {
    captureHandledError(error, {
      tags: { feature: "phi-audit", flow: "session-replay-access" },
      // Document id only — never PHI. sessionId is a Firestore doc id.
      extra: { sessionId, purpose },
    });
    return { logged: false, error };
  }
}
